import React from "react";
import { Eye, Lock, Users } from "lucide-react";

const META = {
  self:     { label: "Visible to you", icon: Lock,  tone: "bg-secondary text-foreground" },
  parents:  { label: "Visible to parents", icon: Eye, tone: "bg-kinly-cream text-foreground" },
  family:   { label: "Visible to family", icon: Users, tone: "bg-secondary text-foreground" },
  circle:   { label: "Visible to circle", icon: Users, tone: "bg-kinly-cream text-foreground" },
  tribe:    { label: "Visible to tribe", icon: Users, tone: "bg-kinly-cream text-foreground" },
};

export function VisibilityBanner({ ring = "family" }) {
  const m = META[ring] || META.family;
  const Icon = m.icon;
  return (
    <span
      data-testid={`visibility-${ring}`}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium tracking-wide ${m.tone}`}
    >
      <Icon className="w-3 h-3" strokeWidth={2} />
      {m.label}
    </span>
  );
}

/**
 * Legacy MemberDot — pure colour swatch. Kept for non-person uses
 * (categories, status pills). For people, prefer <MemberAvatar /> which
 * shows the profile picture with the colour as a thin border so the user
 * stays recognisable AND we keep the colour as a visual cue.
 */
export function MemberDot({ color = "#8FA28F", size = 10, className = "" }) {
  return (
    <span
      data-testid="member-dot"
      className={`inline-block rounded-full shrink-0 ${className}`}
      style={{ width: size, height: size, backgroundColor: color }}
    />
  );
}

/**
 * MemberAvatar — the canonical "this is a person" widget across the app.
 *
 * - Renders the member's `avatar_url` when present.
 * - Falls back to the first letter of their display name on a circle
 *   filled with their colour.
 * - In BOTH cases the member's colour is also a 2px ring around the
 *   circle so the colour stays usable as an at-a-glance cue even with
 *   a photo loaded.
 *
 * Sizes are chosen to be a touch larger than the legacy dots so a
 * photo is actually recognisable (the user explicitly asked for this).
 *
 * Pass `member` directly OR pass `name`/`color`/`url` discretely — both
 * call sites exist across the codebase.
 */
export function MemberAvatar({
  member,
  name,
  color,
  url,
  size = 28,
  className = "",
  ring = true,
  testid,
}) {
  const m = member || {};
  const _name = name ?? m.display_name ?? "";
  const _color = color ?? m.color ?? "#8FA28F";
  const _url = url ?? m.avatar_url ?? null;
  const initial = (_name || "?").charAt(0).toUpperCase();
  const ringW = ring ? 2 : 0;
  const inner = size - ringW * 2;
  return (
    <span
      data-testid={testid || "member-avatar"}
      className={`inline-flex items-center justify-center rounded-full shrink-0 ${className}`}
      style={{
        width: size,
        height: size,
        // The colour ring sits OUTSIDE the photo so it's visible even
        // when the photo fills the whole circle.
        background: ring ? _color : "transparent",
        padding: ringW,
      }}
      aria-label={_name}
    >
      {_url ? (
        <img
          src={_url}
          alt=""
          className="rounded-full object-cover"
          style={{ width: inner, height: inner }}
          draggable={false}
        />
      ) : (
        <span
          className="rounded-full flex items-center justify-center text-background font-display font-semibold"
          style={{
            width: inner,
            height: inner,
            backgroundColor: _color,
            fontSize: Math.max(10, Math.floor(inner * 0.42)),
          }}
        >
          {initial}
        </span>
      )}
    </span>
  );
}
